import { useState, useEffect, useCallback, useDeferredValue } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Menu, Button, Dropdown, Modal, Form, Input, message, Avatar, Drawer, Badge, Popover, Spin } from 'antd'
import {
  LayoutDashboard, Users, DollarSign, MonitorDot, BarChart3,
  RotateCcw, Network, ShieldCheck, Import,
  UserCog, PanelLeftClose, PanelLeftOpen, Bell, KeyRound, LogOut,
  ClipboardCheck, ClipboardList, Landmark, Globe, FileSearch, Activity,
  Menu as MenuIcon, AlertTriangle, Power, Scale, TrendingUp,
  CreditCard, PieChart, Search, Wallet, WalletCards, ShieldAlert, ExternalLink,
  ArrowLeft, Trash2,
} from 'lucide-react'
import { useAuth } from '../stores/auth'
import { api } from '../api/client'
import { tokens } from '../theme/tokens'
import ThemeModeSelect from '../theme/ThemeModeSelect'
import { roleMenuConfig } from '../utils/permissions'
import { resolveCardPlatformMenuKey } from '../utils/cardPlatformRoutes'
import {
  getSidebarGroupsForPaths,
  matchesSidebarSearch,
  searchSidebarGroups,
  type SidebarIconKey,
} from '../utils/sidebarNavigation'
import {
  desktopSidebarStyle,
  desktopViewportStyle,
  desktopWorkspaceStyle,
  scrollableContentStyle,
  scrollableMenuStyle,
} from './mainLayoutStyles'
import {
  HOME_PAGE_HISTORY_ENTRY,
  addPageHistoryEntry,
  createPageHistoryEntry,
  readPageHistory,
  writePageHistory,
  type PageHistoryEntry,
} from '../utils/pageHistory'
import './mobileWorkspace.css'
import '../pages/AccountAnalysis.css'

const { Header, Sider, Content } = Layout

const roleLabels: Record<string, string> = {
  founder: '创始人',
  operator: '运营',
  business: '商务',
  assistant: '助理',
  finance: '财务',
}

interface NotificationSummary {
  total: number
  approvals: {
    visible: boolean
    pending: number
    unprocessed: number
    total: number
  }
  finance: {
    visible: boolean
    unassigned_payments: number
  }
  refunds: {
    visible: boolean
    action_required: number
  }
}

const homeMenuItem = { key: '/', icon: <LayoutDashboard size={18} />, label: '首页' }

const sidebarIconComponents: Record<SidebarIconKey, typeof LayoutDashboard> = {
  'activity': Activity,
  'alert-triangle': AlertTriangle,
  'bar-chart': BarChart3,
  'clipboard-check': ClipboardCheck,
  'clipboard-list': ClipboardList,
  'credit-card': CreditCard,
  'dollar-sign': DollarSign,
  'external-link': ExternalLink,
  'file-search': FileSearch,
  'globe': Globe,
  'import': Import,
  'landmark': Landmark,
  'monitor': MonitorDot,
  'network': Network,
  'pie-chart': PieChart,
  'power': Power,
  'rotate-ccw': RotateCcw,
  'scale': Scale,
  'shield-alert': ShieldAlert,
  'shield-check': ShieldCheck,
  'trending-up': TrendingUp,
  'user-cog': UserCog,
  'users': Users,
  'wallet': Wallet,
  'wallet-cards': WalletCards,
}

const sidebarIcon = (name: SidebarIconKey) => {
  const Icon = sidebarIconComponents[name]
  return <Icon size={18} />
}

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const [menuSearch, setMenuSearch] = useState('')
  const deferredMenuSearch = useDeferredValue(menuSearch)
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  )
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { displayName, role, logout } = useAuth()
  const [pwdOpen, setPwdOpen] = useState(false)
  const [pwdForm] = Form.useForm()
  const [msg, contextHolder] = message.useMessage()
  const [pwdLoading, setPwdLoading] = useState(false)
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [notificationSummary, setNotificationSummary] = useState<NotificationSummary | null>(null)
  const [notificationLoading, setNotificationLoading] = useState(true)
  const [pageHistory, setPageHistory] = useState<PageHistoryEntry[]>(() => (
    typeof window === 'undefined' ? [] : readPageHistory(window.sessionStorage)
  ))
  const canViewNotifications = role === 'founder' || role === 'finance' || role === 'operator'

  const loadNotificationSummary = useCallback(async (showLoading = false) => {
    if (!canViewNotifications) return
    if (showLoading) setNotificationLoading(true)
    try {
      const summary = await api.get<NotificationSummary>('/notifications/summary')
      setNotificationSummary(summary)
    } catch {
      // 顶栏提醒失败不打断当前页面；下次轮询或打开面板时自动重试。
    } finally {
      setNotificationLoading(false)
    }
  }, [canViewNotifications])

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const onChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches)
      if (!e.matches) setMobileDrawerOpen(false)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (collapsed && !isMobile) setMenuSearch('')
  }, [collapsed, isMobile])

  useEffect(() => {
    const entry = createPageHistoryEntry(location.pathname, location.search, location.hash)
    setPageHistory(current => addPageHistoryEntry(current, entry))
  }, [location.hash, location.pathname, location.search])

  useEffect(() => {
    writePageHistory(window.sessionStorage, pageHistory)
  }, [pageHistory])

  useEffect(() => {
    if (!canViewNotifications) {
      setNotificationSummary(null)
      setNotificationLoading(false)
      return
    }
    loadNotificationSummary(true)
    const intervalId = window.setInterval(() => loadNotificationSummary(), 60_000)
    const refreshOnFocus = () => loadNotificationSummary()
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') loadNotificationSummary()
    }
    window.addEventListener('focus', refreshOnFocus)
    window.addEventListener('apex:notifications-refresh', refreshOnFocus)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', refreshOnFocus)
      window.removeEventListener('apex:notifications-refresh', refreshOnFocus)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [canViewNotifications, loadNotificationSummary])

  const allowedPaths = roleMenuConfig[role || ''] || ['/']
  const mobileBusinessPath = ['/opening-orders', '/customers', '/payments']
    .find(path => allowedPaths.includes(path)) || '/'
  const sidebarNotificationCount = (key: string) => {
    if (key === '/payments') {
      return notificationSummary?.finance?.visible
        ? notificationSummary.finance.unassigned_payments
        : 0
    }
    if (key === '/refunds') {
      return notificationSummary?.refunds?.visible
        ? notificationSummary.refunds.action_required
        : 0
    }
    if (key === '/approvals') {
      return notificationSummary?.approvals?.visible
        ? notificationSummary.approvals.total
        : 0
    }
    return 0
  }
  const sidebarNotificationLabel = (label: string, count: number) => (
    <span className="sidebar-menu-label">
      <span>{label}</span>
      {count > 0 && (
        <span className="sidebar-notification-count" aria-label={`${count} 条待办`}>
          {count > 99 ? '99+' : count}
        </span>
      )}
    </span>
  )
  const sidebarNotificationIcon = (icon: React.ReactNode, count: number) => (
    count > 0
      ? <Badge dot offset={[-1, 2]}>{icon}</Badge>
      : icon
  )
  const searchedGroups = searchSidebarGroups(
    getSidebarGroupsForPaths(allowedPaths),
    deferredMenuSearch,
  )
  const isMenuSearching = deferredMenuSearch.replace(/\s+/g, '').length > 0
  const filteredGroups = searchedGroups
    .map(group => {
      const groupCount = group.children.reduce(
        (total, child) => total + sidebarNotificationCount(child.key),
        0,
      )
      return {
        ...group,
        icon: sidebarNotificationIcon(sidebarIcon(group.icon), groupCount),
        label: sidebarNotificationLabel(group.label, groupCount),
        children: group.children
          .map(child => {
            const childCount = sidebarNotificationCount(child.key)
            return {
              ...child,
              icon: sidebarNotificationIcon(sidebarIcon(child.icon), childCount),
              label: sidebarNotificationLabel(child.label, childCount),
            }
          }),
      }
    })

  const filteredMenuItems: any[] = [
    ...(matchesSidebarSearch('首页', deferredMenuSearch) ? [homeMenuItem] : []),
    ...filteredGroups,
  ]

  const handleChangePwd = async (values: { old_password: string; new_password: string }) => {
    setPwdLoading(true)
    try {
      await api.put('/auth/me/password', values)
      msg.success('密码修改成功')
      setPwdOpen(false)
      pwdForm.resetFields()
    } catch (e: any) {
      msg.error(e.message)
    } finally {
      setPwdLoading(false)
    }
  }

  const sidebarInner = (onItemClick?: () => void) => (
    <div className="apex-sidebar-inner">
      <div
        style={{
          height: collapsed && !isMobile ? 64 : 96, display: 'flex', alignItems: 'center',
          justifyContent: 'center',
          padding: collapsed && !isMobile ? '0' : '0 12px',
          cursor: 'pointer', overflow: 'hidden',
          borderBottom: `1px solid ${tokens.colors.borderLight}`,
        }}
        onClick={() => { setMenuSearch(''); navigate('/'); onItemClick?.() }}
      >
        {collapsed && !isMobile
          ? <img className="apex-brand-logo" src="/logo-square-trimmed.png" alt="AT" style={{ width: 46, height: 'auto', maxWidth: '100%', objectFit: 'contain' }} />
          : <img className="apex-brand-logo" src="/logo-trimmed.png" alt="ApexTribe" style={{ width: '88%', maxWidth: 210, height: 'auto', objectFit: 'contain' }} />
        }
      </div>

      {!(collapsed && !isMobile) && (
        <div className="sidebar-menu-search">
          <Input
            value={menuSearch}
            onChange={(event) => setMenuSearch(event.target.value)}
            prefix={<Search size={16} aria-hidden="true" />}
            placeholder="搜索功能"
            allowClear
            aria-label="搜索侧边栏功能"
          />
        </div>
      )}

      <div style={{ ...scrollableMenuStyle, padding: '8px 8px 0' }}>
        {filteredMenuItems.length > 0 ? (
          <Menu
            mode="inline"
            selectedKeys={[resolveCardPlatformMenuKey(location.pathname)]}
            openKeys={isMenuSearching ? searchedGroups.map(group => group.key) : undefined}
            items={filteredMenuItems}
            onClick={({ key }) => { setMenuSearch(''); navigate(key); onItemClick?.() }}
            style={{ border: 'none' }}
          />
        ) : (
          <div className="sidebar-search-empty" role="status">
            未找到相关功能
          </div>
        )}
      </div>

      {!(collapsed && !isMobile) && (
        <div style={{
          flex: '0 0 auto',
          padding: '16px 20px', borderTop: `1px solid ${tokens.colors.borderLight}`,
          background: tokens.colors.background,
        }}>
          {isMobile && (
            <div className="mobile-sidebar-theme">
              <span>外观</span>
              <ThemeModeSelect />
            </div>
          )}
          <div style={{ fontSize: 11, color: tokens.colors.textPlaceholder, textAlign: 'center' }}>
            ApexTribe v1.0
          </div>
        </div>
      )}
    </div>
  )

  const openNotificationTarget = (path: string) => {
    setNotificationOpen(false)
    navigate(path)
  }

  const clearPageHistory = () => {
    const next = [HOME_PAGE_HISTORY_ENTRY]
    setPageHistory(next)
    navigate('/', { replace: true })
  }

  const revisitPage = (entry: PageHistoryEntry) => {
    navigate(entry.href, { replace: true })
  }

  const notificationContent = (
    <div style={{ width: isMobile ? 276 : 320 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 4px 12px', borderBottom: `1px solid ${tokens.colors.borderLight}`,
      }}>
        <div>
          <div style={{ color: tokens.colors.text, fontSize: 15, fontWeight: 650 }}>通知提醒</div>
          <div style={{ color: tokens.colors.textMuted, fontSize: 12, marginTop: 2 }}>需要关注的待办事项</div>
        </div>
        {notificationSummary && (
          <Badge count={notificationSummary.total} showZero color={notificationSummary.total ? tokens.colors.errorLight : tokens.colors.borderLight} style={{ color: notificationSummary.total ? tokens.colors.errorText : tokens.colors.textSecondary }} />
        )}
      </div>

      {notificationLoading && !notificationSummary ? (
        <div style={{ height: 132, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="small" />
        </div>
      ) : (
        <div style={{ paddingTop: 8 }}>
          {notificationSummary?.approvals?.visible && (
            <div style={{ marginBottom: 8 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', color: tokens.colors.textSecondary, fontSize: 12, fontWeight: 600,
              }}>
                <ClipboardCheck size={16} />
                <span>审批管理</span>
                <span style={{ marginLeft: 'auto' }}>{notificationSummary.approvals.total} 条</span>
              </div>
              <button
                type="button"
                className="notification-row"
                onClick={() => openNotificationTarget('/approvals?tab=unprocessed')}
              >
                <span>未处理事项</span>
                <Badge count={notificationSummary.approvals.unprocessed} showZero color={notificationSummary.approvals.unprocessed ? tokens.colors.errorLight : tokens.colors.borderLight} style={{ color: notificationSummary.approvals.unprocessed ? tokens.colors.errorText : tokens.colors.textSecondary }} />
              </button>
              <button
                type="button"
                className="notification-row"
                onClick={() => openNotificationTarget('/approvals?tab=pending')}
              >
                <span>待审批事项</span>
                <Badge count={notificationSummary.approvals.pending} showZero color={notificationSummary.approvals.pending ? tokens.colors.warningLight : tokens.colors.borderLight} style={{ color: notificationSummary.approvals.pending ? tokens.colors.warningText : tokens.colors.textSecondary }} />
              </button>
            </div>
          )}

          {notificationSummary?.finance?.visible && (
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', color: tokens.colors.textSecondary, fontSize: 12, fontWeight: 600,
              }}>
                <Wallet size={16} />
                <span>资金管理</span>
              </div>
              <button
                type="button"
                className="notification-row"
                onClick={() => openNotificationTarget('/payments?tab=unassigned')}
              >
                <span>未认领付款</span>
                <Badge count={notificationSummary.finance.unassigned_payments} showZero color={notificationSummary.finance.unassigned_payments ? tokens.colors.errorLight : tokens.colors.borderLight} style={{ color: notificationSummary.finance.unassigned_payments ? tokens.colors.errorText : tokens.colors.textSecondary }} />
              </button>
            </div>
          )}

          {notificationSummary?.refunds?.visible && (
            <div style={{ marginTop: 8 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', color: tokens.colors.textSecondary, fontSize: 12, fontWeight: 600,
              }}>
                <RotateCcw size={16} />
                <span>退款管理</span>
              </div>
              <button
                type="button"
                className="notification-row"
                aria-label={`退款管理，${notificationSummary.refunds.action_required} 条待你处理`}
                onClick={() => openNotificationTarget('/refunds')}
              >
                <span>待你处理</span>
                <Badge count={notificationSummary.refunds.action_required} showZero color={notificationSummary.refunds.action_required ? tokens.colors.warningLight : tokens.colors.borderLight} style={{ color: notificationSummary.refunds.action_required ? tokens.colors.warningText : tokens.colors.textSecondary }} />
              </button>
            </div>
          )}

          {notificationSummary?.total === 0 && (
            <div style={{ padding: '12px 8px 4px', textAlign: 'center', color: tokens.colors.textMuted, fontSize: 12 }}>
              当前没有需要处理的事项
            </div>
          )}
        </div>
      )}
    </div>
  )

  const currentPageTitle = createPageHistoryEntry(
    location.pathname,
    location.search,
    location.hash,
  ).title
  const isMobileHomePage = isMobile && location.pathname === '/'
  const isMobileAccountPage = isMobile && location.pathname === '/ad-accounts'
  const isMobileMediaTodoPage = isMobile && location.pathname === '/media-todos'
  const isMobileOpeningOrdersPage = isMobile && location.pathname === '/opening-orders'
  const isMobileWasabiPage = isMobile && location.pathname.startsWith('/wasabi')
  const isMobilePhotonPayPage = isMobile && location.pathname.startsWith('/photonpay')
  const isMobileCardPlatformPage = isMobileWasabiPage || isMobilePhotonPayPage
  const isMobileBusinessPage = isMobileMediaTodoPage || isMobileOpeningOrdersPage
  const isMobileOtherPage = isMobile && !isMobileHomePage && !isMobileAccountPage && !isMobileBusinessPage && !isMobileCardPlatformPage
  const isMobileMorePage = isMobileCardPlatformPage || isMobileOtherPage
  const isMobileImmersivePage = isMobileHomePage || isMobileAccountPage || isMobileBusinessPage || isMobileMorePage
  const contentRouteClass = `apex-route-${location.pathname.replace(/^\/+/, '').replace(/[^a-zA-Z0-9]+/g, '-') || 'home'}`

  return (
    <Layout
      className="apex-layout"
      style={isMobile ? { minHeight: '100vh' } : desktopViewportStyle}
    >
      {contextHolder}
      {!isMobile && (
        <Sider
          trigger={null} collapsible collapsed={collapsed} width={240}
          style={{
            ...desktopSidebarStyle,
            background: tokens.colors.surface,
            borderRight: `1px solid ${tokens.colors.border}`,
            boxShadow: tokens.shadows.sidebar,
          }}
        >
          {sidebarInner()}
        </Sider>
      )}

      {isMobile && (
        <Drawer
          placement="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          width={260}
          closable={false}
          bodyStyle={{ padding: 0, background: tokens.colors.surface, position: 'relative' }}
          headerStyle={{ display: 'none' }}
        >
          {sidebarInner(() => setMobileDrawerOpen(false))}
        </Drawer>
      )}

      <Layout style={isMobile ? undefined : desktopWorkspaceStyle}>
        <Header className="apex-header" style={{
          padding: isMobile ? '0 12px' : '0 28px', height: isMobile ? 56 : 64, display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          background: tokens.colors.surface,
          borderBottom: `1px solid ${tokens.colors.border}`,
          boxShadow: tokens.shadows.header,
          lineHeight: 'normal',
        }}>
          {isMobileImmersivePage ? (
            <button
              type="button"
              className="apex-mobile-brand"
              aria-label="返回首页"
              onClick={() => navigate('/')}
            >
              <img className="apex-brand-logo" src="/logo-trimmed.png" alt="ApexTribe" />
            </button>
          ) : (
            <Button
              type="text"
              onClick={() => isMobile ? setMobileDrawerOpen(true) : setCollapsed(!collapsed)}
              title={isMobile ? '打开侧边菜单' : (collapsed ? '展开侧边菜单' : '收起侧边菜单')}
              aria-label={isMobile ? '打开侧边菜单' : (collapsed ? '展开侧边菜单' : '收起侧边菜单')}
              style={{
                width: isMobile ? 44 : 48,
                height: isMobile ? 44 : 48,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: tokens.colors.textSecondary,
                background: tokens.colors.background,
                border: `1px solid ${tokens.colors.borderLight}`,
                borderRadius: tokens.radius.lg,
              }}
            >
              {isMobile ? <MenuIcon size={22} /> : (collapsed ? <PanelLeftOpen size={24} /> : <PanelLeftClose size={24} />)}
            </Button>
          )}

          {isMobile && !isMobileImmersivePage && <div className="mobile-page-title">{currentPageTitle}</div>}

          <nav className="page-history" aria-label="最近访问页面">
            <Button
              type="text"
              className="page-history-back"
              icon={<ArrowLeft size={17} />}
              disabled={pageHistory.length <= 1}
              onClick={() => {
                const previousPage = pageHistory[pageHistory.length - 2]
                if (previousPage) revisitPage(previousPage)
              }}
              title="返回上一页"
              aria-label="返回上一页"
            />
            <div className="page-history-tabs">
              {pageHistory.map(entry => {
                const active = entry.key === location.pathname
                return (
                  <button
                    type="button"
                    key={entry.key}
                    className={`page-history-tab${active ? ' is-active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                    title={entry.title}
                    onClick={() => {
                      if (!active || entry.href !== `${location.pathname}${location.search}${location.hash}`) {
                        revisitPage(entry)
                      }
                    }}
                  >
                    {entry.title}
                  </button>
                )
              })}
            </div>
            <Button
              type="text"
              className="page-history-clear"
              icon={<Trash2 size={15} />}
              onClick={clearPageHistory}
              title="清空访问记录并返回首页"
              aria-label="清空访问记录并返回首页"
            >
              清空
            </Button>
          </nav>

          <div className="apex-header-actions">
            {!isMobile && <ThemeModeSelect />}
            {canViewNotifications && (
              <Popover
                trigger="click"
                placement="bottomRight"
                arrow={false}
                open={notificationOpen}
                onOpenChange={(open) => {
                  setNotificationOpen(open)
                  if (open) loadNotificationSummary()
                }}
                content={notificationContent}
              >
                <Badge count={notificationSummary?.total || 0} overflowCount={99} offset={[-2, 3]}>
                  <Button
                    type="text"
                    title="通知提醒"
                    aria-label={`通知提醒${notificationSummary?.total ? `，${notificationSummary.total} 条待办` : ''}`}
                    style={{
                      width: isMobile ? 44 : 48, height: isMobile ? 44 : 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: notificationSummary?.total ? tokens.colors.primary : tokens.colors.textSecondary,
                      background: notificationSummary?.total ? `color-mix(in srgb, ${tokens.colors.primary} 5%, ${tokens.colors.surface})` : tokens.colors.background,
                      border: `1px solid ${notificationSummary?.total ? `color-mix(in srgb, ${tokens.colors.primary} 19%, ${tokens.colors.surface})` : tokens.colors.borderLight}`,
                      borderRadius: tokens.radius.lg,
                    }}
                  >
                    <Bell size={isMobile ? 21 : 24} />
                  </Button>
                </Badge>
              </Popover>
            )}

            <Dropdown
              menu={{
                items: [
                  {
                    key: 'info',
                    label: (
                      <div style={{ padding: '4px 0' }}>
                        <div style={{ fontWeight: 600, color: tokens.colors.text }}>{displayName}</div>
                        <div style={{ fontSize: 12, color: tokens.colors.textMuted }}>{roleLabels[role || ''] || role}</div>
                      </div>
                    ),
                    disabled: true,
                  },
                  { type: 'divider' },
                  { key: 'password', icon: <KeyRound size={14} />, label: '修改密码' },
                  { type: 'divider' },
                  { key: 'logout', icon: <LogOut size={14} />, label: '退出登录', danger: true },
                ],
                onClick: async ({ key }) => {
                  if (key === 'logout') { await logout(); navigate('/login', { replace: true }) }
                  else if (key === 'password') { setPwdOpen(true) }
                },
              }}
            >
              <div className="apex-profile-trigger" style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: isMobile ? '4px' : '4px 12px 4px 4px', borderRadius: tokens.radius.md,
                cursor: 'pointer', transition: tokens.transition.fast,
              }}
              onMouseEnter={e => e.currentTarget.style.background = tokens.colors.borderLight}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Avatar
                  size={34}
                  style={{
                    background: tokens.gradients.primary,
                    fontSize: 14, fontWeight: 600,
                  }}
                >
                  {(displayName || 'U')[0].toUpperCase()}
                </Avatar>
                {!isMobile && (
                  <div style={{ lineHeight: 1.3 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: tokens.colors.text }}>{displayName}</div>
                    <div style={{ fontSize: 11, color: tokens.colors.textMuted }}>{roleLabels[role || ''] || role}</div>
                  </div>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className={`apex-content ${contentRouteClass}`} style={{
          ...(!isMobile ? scrollableContentStyle : {}),
          margin: isMobile ? 0 : 24,
          padding: isMobileImmersivePage ? '12px 12px 84px' : isMobile ? 12 : tokens.spacing.page,
          background: isMobile ? 'transparent' : tokens.colors.surface,
          borderRadius: isMobile ? 0 : tokens.radius.lg,
          border: isMobile ? 'none' : `1px solid ${tokens.colors.border}`,
          boxShadow: isMobile ? 'none' : tokens.shadows.card,
          minHeight: isMobile ? 'calc(100vh - 56px)' : 0,
          overflow: isMobile ? 'visible' : 'auto',
        }}>
          <Outlet />
        </Content>
      </Layout>

      {isMobileImmersivePage && (
        <nav className="apex-mobile-tabbar" aria-label="移动端主导航">
          <button type="button" className={isMobileHomePage ? 'is-active' : ''} aria-current={isMobileHomePage ? 'page' : undefined} onClick={() => navigate('/')}>
            <LayoutDashboard size={20} aria-hidden="true" />
            <span>首页</span>
          </button>
          <button type="button" className={isMobileAccountPage ? 'is-active' : ''} aria-current={isMobileAccountPage ? 'page' : undefined} onClick={() => navigate('/ad-accounts')}>
            <Users size={20} aria-hidden="true" />
            <span>账户</span>
          </button>
          <button type="button" className={isMobileBusinessPage ? 'is-active' : ''} aria-current={isMobileBusinessPage ? 'page' : undefined} onClick={() => navigate(isMobileBusinessPage ? location.pathname : mobileBusinessPath)}>
            <Badge count={notificationSummary?.total || 0} size="small" offset={[5, -2]}>
              <ClipboardList size={20} aria-hidden="true" />
            </Badge>
            <span>业务</span>
          </button>
          <button
            type="button"
            className={isMobileMorePage ? 'is-active' : ''}
            aria-current={isMobileMorePage ? 'page' : undefined}
            onClick={() => setMobileDrawerOpen(true)}
          >
            <MenuIcon size={20} aria-hidden="true" />
            <span>更多</span>
          </button>
        </nav>
      )}

      <Modal
        title="修改密码" open={pwdOpen}
        onCancel={() => { setPwdOpen(false); pwdForm.resetFields() }}
        onOk={() => pwdForm.submit()} confirmLoading={pwdLoading}
      >
        <Form form={pwdForm} layout="vertical" onFinish={handleChangePwd}>
          <Form.Item name="old_password" label="当前密码" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="new_password" label="新密码" rules={[{ required: true }, { min: 6, message: '密码至少6位' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="confirm" label="确认新密码" dependencies={['new_password']} rules={[
            { required: true, message: '请确认新密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('new_password') === value) return Promise.resolve()
                return Promise.reject(new Error('两次密码不一致'))
              },
            }),
          ]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      <style>{`
        .apex-layout { background: ${tokens.colors.background} !important; }
        .apex-layout .ant-layout { background: ${tokens.colors.background} !important; }
        .apex-sidebar-inner {
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 0;
          overflow: hidden;
        }

        .page-history {
          min-width: 0;
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 20px;
        }
        .page-history-back,
        .page-history-clear {
          flex: 0 0 auto;
          height: 36px;
          border: 1px solid ${tokens.colors.borderLight};
          border-radius: ${tokens.radius.md}px;
          color: ${tokens.colors.textSecondary};
        }
        .page-history-back {
          width: 36px;
          padding: 0;
        }
        .page-history-clear {
          padding: 0 10px;
        }
        .page-history-tabs {
          min-width: 0;
          flex: 1;
          display: flex;
          align-items: center;
          gap: 4px;
          overflow-x: auto;
          scrollbar-width: none;
        }
        .page-history-tabs::-webkit-scrollbar {
          display: none;
        }
        .page-history-tab {
          max-width: 160px;
          height: 36px;
          padding: 0 12px;
          flex: 0 0 auto;
          overflow: hidden;
          border: 0;
          border-radius: ${tokens.radius.md}px;
          background: transparent;
          color: ${tokens.colors.textSecondary};
          font: inherit;
          font-size: 13px;
          line-height: 36px;
          text-overflow: ellipsis;
          white-space: nowrap;
          cursor: pointer;
          transition: ${tokens.transition.fast};
        }
        .page-history-tab:hover,
        .page-history-tab:focus-visible {
          background: ${tokens.colors.background};
          color: ${tokens.colors.primary};
          outline: none;
        }
        .page-history-tab.is-active {
          background: color-mix(in srgb, ${tokens.colors.primary} 5%, ${tokens.colors.surface});
          color: ${tokens.colors.primary};
          font-weight: 600;
        }

        .mobile-page-title {
          display: none;
        }

        .apex-mobile-brand,
        .apex-mobile-tabbar {
          display: none;
        }

        .apex-header-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .mobile-sidebar-theme {
          min-height: 44px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
          color: ${tokens.colors.textSecondary};
          font-size: 13px;
          font-weight: 600;
        }

        .sidebar-menu-search {
          flex: 0 0 auto;
          padding: 10px 10px 0;
        }
        .sidebar-menu-search .ant-input-affix-wrapper {
          min-height: 38px;
          border-color: ${tokens.colors.border};
          border-radius: ${tokens.radius.md}px;
          box-shadow: none;
        }
        .sidebar-menu-search .ant-input-affix-wrapper:focus,
        .sidebar-menu-search .ant-input-affix-wrapper-focused {
          border-color: ${tokens.colors.primary};
          box-shadow: 0 0 0 2px color-mix(in srgb, ${tokens.colors.primary} 8%, transparent);
        }
        .sidebar-menu-search svg {
          color: ${tokens.colors.textMuted};
        }
        .sidebar-search-empty {
          padding: 28px 12px;
          color: ${tokens.colors.textMuted};
          font-size: 13px;
          text-align: center;
        }

        .apex-layout .ant-layout-sider .ant-menu-item {
          height: 44px !important;
          line-height: 44px !important;
          border-radius: 10px !important;
          margin: 2px 0 !important;
          color: ${tokens.colors.textSecondary} !important;
          font-size: 13.5px;
          transition: ${tokens.transition.default};
        }
        .apex-layout .ant-layout-sider .ant-menu-item:hover {
          color: ${tokens.colors.primary} !important;
          background: color-mix(in srgb, ${tokens.colors.primary} 3%, ${tokens.colors.surface}) !important;
        }
        .apex-layout .ant-layout-sider .ant-menu-item-selected {
          color: #fff !important;
          background: ${tokens.gradients.sidebar} !important;
          box-shadow: 0 3px 12px rgba(79,70,229,0.3);
          font-weight: 500;
        }
        .apex-layout .ant-layout-sider .ant-menu-item-selected svg {
          color: #fff !important;
        }
        .apex-layout .ant-layout-sider .ant-menu-item svg {
          color: ${tokens.colors.textMuted};
          transition: color 0.2s;
        }
        .apex-layout .ant-layout-sider .ant-menu-item:hover svg {
          color: ${tokens.colors.primary} !important;
        }

        .sidebar-menu-label {
          width: auto;
          min-width: 0;
          display: inline-flex;
          align-items: center;
          justify-content: flex-start;
          gap: 6px;
        }
        .apex-layout .ant-menu-submenu-title .sidebar-menu-label {
          box-sizing: border-box;
          max-width: calc(100% - 24px);
        }
        .sidebar-notification-count {
          min-width: 18px;
          height: 18px;
          padding: 0 5px;
          flex: 0 0 auto;
          border-radius: ${tokens.radius.pill}px;
          background: ${tokens.colors.errorLight};
          box-shadow: 0 2px 6px rgba(239, 68, 68, 0.28);
          color: ${tokens.colors.errorText};
          font-size: 10px;
          font-weight: 700;
          line-height: 18px;
          text-align: center;
          font-variant-numeric: tabular-nums;
        }
        .apex-layout .ant-menu-item-selected .sidebar-notification-count {
          background: ${tokens.colors.surface};
          color: ${tokens.colors.errorText};
          box-shadow: none;
        }

        .notification-row {
          width: 100%;
          min-height: 42px;
          padding: 8px 10px 8px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          border: 0;
          border-radius: ${tokens.radius.md}px;
          background: transparent;
          color: ${tokens.colors.text};
          font: inherit;
          font-size: 13px;
          text-align: left;
          cursor: pointer;
          transition: ${tokens.transition.fast};
        }
        .notification-row:hover,
        .notification-row:focus-visible {
          color: ${tokens.colors.primary};
          background: color-mix(in srgb, ${tokens.colors.primary} 4%, ${tokens.colors.surface});
          outline: none;
        }

        /* Global table refinements */
        .ant-table-thead > tr > th {
          background: ${tokens.colors.background} !important;
          color: ${tokens.colors.textSecondary} !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          border-bottom: 1px solid ${tokens.colors.border} !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid ${tokens.colors.borderLight} !important;
          font-size: 13.5px;
        }
        .ant-table-tbody > tr:hover > td {
          background: ${tokens.colors.surfaceHover} !important;
        }

        /* Modal refinements */
        .ant-modal-header {
          border-bottom: 1px solid ${tokens.colors.borderLight} !important;
        }
        .ant-modal-title {
          font-weight: 600 !important;
          color: ${tokens.colors.text} !important;
        }

        /* Button refinements */
        .ant-btn-primary {
          box-shadow: ${tokens.shadows.button};
        }
        .ant-btn-primary:hover {
          box-shadow: ${tokens.shadows.buttonHover} !important;
          transform: translateY(-0.5px);
        }

        /* Tag refinements */
        .ant-tag {
          border: none !important;
          border-radius: 6px !important;
          font-weight: 500 !important;
          padding: 2px 10px !important;
        }

        /* Scrollbar */
        .ant-layout-sider ::-webkit-scrollbar { width: 4px; }
        .ant-layout-sider ::-webkit-scrollbar-thumb {
          background: ${tokens.colors.border}; border-radius: 4px;
        }

        /* ===== Mobile adaptation (<= 768px) ===== */
        @media (max-width: 768px) {
          .apex-header {
            position: sticky;
            top: 0;
            z-index: 20;
          }

          .page-history {
            display: none;
          }

          .mobile-page-title {
            min-width: 0;
            display: block;
            flex: 1;
            margin: 0 10px;
            overflow: hidden;
            color: ${tokens.colors.text};
            font-size: 16px;
            font-weight: 700;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .apex-mobile-brand {
            width: 118px;
            min-height: 44px;
            padding: 0;
            display: inline-flex;
            align-items: center;
            border: 0;
            background: transparent;
            cursor: pointer;
          }

          .apex-mobile-brand img {
            display: block;
            width: 112px;
            height: auto;
            object-fit: contain;
          }

          .apex-mobile-tabbar {
            position: fixed;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 30;
            min-height: calc(64px + env(safe-area-inset-bottom));
            padding: 6px 10px env(safe-area-inset-bottom);
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            border-top: 1px solid ${tokens.colors.border};
            background: color-mix(in srgb, ${tokens.colors.surface} 96%, transparent);
            box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.06);
            backdrop-filter: blur(14px);
          }

          .apex-mobile-tabbar button {
            min-width: 0;
            min-height: 52px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 3px;
            border: 0;
            border-radius: 10px;
            background: transparent;
            color: ${tokens.colors.textMuted};
            font: inherit;
            font-size: 11px;
            cursor: pointer;
          }

          .apex-mobile-tabbar button:active,
          .apex-mobile-tabbar button.is-active {
            background: color-mix(in srgb, ${tokens.colors.primary} 7%, transparent);
            color: ${tokens.colors.primary};
          }

          .apex-mobile-tabbar button:focus-visible {
            outline: 2px solid ${tokens.colors.primary};
            outline-offset: -2px;
          }

          .apex-header-actions {
            flex: 0 0 auto;
            gap: 6px;
          }

          .apex-profile-trigger {
            min-width: 42px;
            min-height: 44px;
            justify-content: center;
          }

          /* Tables scroll horizontally instead of clipping */
          .ant-table-wrapper, .ant-table { overflow-x: auto !important; }
          .ant-table-content { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }

          /* Modals full-width with small margin */
          .ant-modal { max-width: calc(100vw - 16px) !important; margin: 8px auto !important; }
          .ant-modal-body { padding: 16px !important; max-height: calc(100vh - 180px); overflow-y: auto; }
          .ant-modal-footer .ant-btn { min-height: 44px; }

          /* Compact form labels */
          .ant-form-vertical .ant-form-item-label > label { font-size: 13px; }

          /* Drawer body scroll */
          .ant-drawer-body { overflow-y: auto; }

          /* Tag smaller on mobile */
          .ant-tag { padding: 1px 6px !important; font-size: 11px !important; }

          /* Card tightened */
          .ant-card-body { padding: 12px !important; }

          /* Header title subtitle allow wrap on tight screens */
          .apex-header h1, .apex-header h2, .apex-header h3, .apex-header h4 { font-size: 16px !important; }
        }
      `}</style>
    </Layout>
  )
}
